import { useIsMutating } from 'react-query';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoading from '@mui/lab/LoadingButton';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Alert from '@mui/material/Alert';
import HighlightOffRoundedIcon from '@mui/icons-material/HighlightOffRounded';
import CircularProgress from '@mui/material/CircularProgress';
import { useMemo } from 'react';
import { useGetPersonRolesAttached } from 'services/v1/TenantMaster/MasterTable/PersonTableDataService';

const ModalContentStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  borderRadius: 1,
  px: 3,
  py: 2,
};

const buttonActionStyle = { width: '100%', textTransform: 'none' };

const closeIconStyle = { color: '#98A2AE', cursor: 'pointer' };

export type ModalDeleteState = {
  title?: string;
  message: string;
  open?: boolean;
  isError?: boolean;
  key?: string | number;
  errorMessage?: string;
};

export interface ModalDeleteProps extends ModalDeleteState {
  visible?: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  onApprove?: () => void;
  userId?: string;
}

export default function ModalDeleteComponent(props: ModalDeleteProps) {
  const isMutating = Boolean(useIsMutating());
  const { t } = useTranslation();
  const { onApprove, onCancel, onClose, visible = false, userId, message, title, isError, errorMessage } = props;

  const { data: userRoles, isLoading: isLoadingRoles } = useGetPersonRolesAttached(userId || '');

  const displayMessage = useMemo(() => {
    let finalMessage = message;
    if (userRoles?.data) {
      if (userRoles.data.roles && userRoles.data.roles.length > 0) {
        const roleListItems = userRoles.data.roles.map((r) => `<li>${r.roleName}</li>`).join('');
        finalMessage += `<br/><br/> <b>User is assigned to the following roles:</b><ul>${roleListItems}</ul>`;
      }
      if (userRoles.data.tenants && userRoles.data.tenants.length > 0) {
        const tenantListItems = userRoles.data.tenants.map((t) => `<li>${t.tenantName}</li>`).join('');
        finalMessage += `<br/><br/> <b>User is assigned to the following tenants:</b><ul>${tenantListItems}</ul>`;
      }
    }
    return finalMessage;
  }, [userRoles, message]);

  return (
    <div>
      <Modal open={visible} onClose={onClose}>
        <Stack direction='column' sx={ModalContentStyle}>
          <Stack justifyContent='space-between' direction='row' alignItems='center'>
            <Typography variant='body1' component='h2' fontWeight='bold' sx={{ color: '#3B4797' }}>
              {t('COMMON.MODAL_DELETE.TITLE')}
            </Typography>
            <IconButton onClick={onClose} component='label' disabled={isMutating}>
              <HighlightOffRoundedIcon sx={closeIconStyle} />
            </IconButton>
          </Stack>
          <Divider sx={{ mb: 1 }} />
          {!isError && title && (
            <Stack alignItems='center' sx={{ py: 2, bgcolor: '#F7F7F7', mt: 2 }}>
              <Typography variant='body1'>{title} </Typography>
            </Stack>
          )}
          <Stack alignItems='center' sx={{ py: 3, width: '100%' }}>
            {isLoadingRoles ? (
              <CircularProgress />
            ) : (
              <>
                {!isError && (
                  <Alert severity='warning' sx={{ width: '100%', maxHeight: '300px', overflowY: 'auto' }}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: displayMessage,
                      }}
                    ></span>
                  </Alert>
                )}
                {isError && (
                  <Alert severity='error' sx={{ width: '100%', mt: 2, backgroundColor: '#FFFBE4', color: '#BC0000' }}>
                    {errorMessage}
                  </Alert>
                )}
              </>
            )}
          </Stack>
          <Divider sx={{ mb: 1 }} />
          {isError ? (
            <Button variant='main-table-panel' sx={buttonActionStyle} onClick={onClose}>
              Close
            </Button>
          ) : (
            <Stack direction='row' justifyContent='center' spacing={2} alignItems='center' sx={{ py: 1 }}>
              <Button disabled={isMutating} variant='main-table-panel' sx={buttonActionStyle} onClick={onCancel}>
                {t('COMMON.BUTTON.CANCEL')}
              </Button>
              <ButtonLoading
                loading={isMutating}
                variant='contained'
                color='error'
                sx={buttonActionStyle}
                onClick={onApprove}
                disabled={isLoadingRoles}
              >
                {t('COMMON.MODAL_DELETE.BUTTON_DELETE')}
              </ButtonLoading>
            </Stack>
          )}
        </Stack>
      </Modal>
    </div>
  );
}
